/**
 * DashboardNotificationsTab — iter90 (Feb 2026) full redesign.
 *
 * Phase 1 operational notification center. Reads from the new
 * `/api/restaurant/alerts/*` endpoints (auto-evaluated from real
 * system state — Stripe / status / profile) instead of the old
 * SMS/WhatsApp log.
 *
 * Renders a categorized, severity-coded list with mark-as-read,
 * mark-all-read, dismiss, and a CTA back to the relevant
 * settings tab. Stays in sync with the dashboard bell badge via
 * the parent's `onChange` callback.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, AlertCircle, Info, CheckCircle2, X, ChevronRight,
  CreditCard, Power, User as UserIcon, BellOff, RefreshCw,
} from 'lucide-react';
import PushNotificationsToggle from '../PushNotificationsToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICON = {
  payment: CreditCard,
  status:  Power,
  account: UserIcon,
};
const CATEGORY_LABEL = {
  payment: 'Payment',
  status:  'Restaurant status',
  account: 'Account',
};
const SEVERITY_STYLE = {
  critical: { ring: 'border-rose-500/60',   bg: 'bg-rose-500/[0.06]',   chip: 'bg-rose-500/15 text-rose-300 border-rose-500/40',   Icon: AlertCircle,    iconColor: 'text-rose-400' },
  high:     { ring: 'border-amber-500/55',  bg: 'bg-amber-500/[0.05]',  chip: 'bg-amber-500/15 text-amber-300 border-amber-500/40', Icon: AlertTriangle,  iconColor: 'text-amber-400' },
  medium:   { ring: 'border-yellow-500/45', bg: 'bg-yellow-500/[0.04]', chip: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40', Icon: Info,         iconColor: 'text-yellow-300' },
  low:      { ring: 'border-zinc-700/70',   bg: 'bg-white/[0.02]',      chip: 'bg-white/5 text-white/65 border-white/15',            Icon: Info,           iconColor: 'text-white/65' },
};

const DashboardNotificationsTab = ({ token, onChange }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/restaurant/alerts`, auth);
      setAlerts(r.data?.alerts || []);
      onChange?.();
    } catch {
      toast.error('Could not load notifications');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/restaurant/alerts/${id}/read`, {}, auth);
      setAlerts((arr) => arr.map((a) => (a.id === id ? { ...a, read: true } : a)));
      onChange?.();
    } finally { setBusyId(null); }
  };

  const dismiss = async (id) => {
    setBusyId(id);
    try {
      await axios.delete(`${API}/restaurant/alerts/${id}`, auth);
      setAlerts((arr) => arr.filter((a) => a.id !== id));
      onChange?.();
    } finally { setBusyId(null); }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/restaurant/alerts/read-all`, {}, auth);
      setAlerts((arr) => arr.map((a) => ({ ...a, read: true })));
      onChange?.();
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Could not mark all as read');
    }
  };

  const unread = alerts.filter((a) => !a.read).length;

  return (
    <div data-testid="dashboard-notifications-tab" className="text-white">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Notifications</h2>
          <p className="mt-1 text-sm text-zinc-400">
            Account, payment, and operational alerts — generated automatically from your restaurant&apos;s real-time state.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 hover:border-yellow-500/40 text-white/70 hover:text-yellow-200 text-[12.5px] font-bold transition-all"
            data-testid="alerts-refresh-btn"
            aria-label="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black text-[12.5px] font-extrabold transition-all"
              data-testid="alerts-mark-all-read-btn"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark all read ({unread})
            </button>
          )}
        </div>
      </div>

      {/* Web Push toggle — sits at the top so the owner enables once and
          subsequent alerts also fire as native browser notifications. */}
      <div className="mb-6">
        <PushNotificationsToggle role="restaurant" token={token} />
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm" data-testid="alerts-loading">Loading…</div>
      ) : alerts.length === 0 ? (
        <div
          data-testid="alerts-empty"
          className="p-10 sm:p-12 rounded-3xl border border-white/8 bg-white/[0.02] text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <BellOff className="w-6 h-6 text-emerald-300" />
          </div>
          <h3 className="mt-5 text-lg font-bold text-white">You&apos;re all caught up.</h3>
          <p className="mt-2 text-[13.5px] text-white/55 max-w-md mx-auto leading-relaxed">
            Important account, payment, and business alerts will appear here. You will also be notified the moment something needs your attention.
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="alerts-list">
          {alerts.map((a) => {
            const sev   = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.medium;
            const CatIcon = CATEGORY_ICON[a.category] || Info;
            const isUnread = !a.read;
            return (
              <li
                key={a.id}
                data-testid={`alert-${a.kind}`}
                data-alert-id={a.id}
                className={`relative rounded-2xl border ${sev.ring} ${sev.bg} p-4 sm:p-5 transition-all ${
                  isUnread ? 'shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)]' : 'opacity-75'
                }`}
              >
                <div className="flex gap-3 sm:gap-4">
                  <div className={`w-10 h-10 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center flex-shrink-0`}>
                    <sev.Icon className={`w-5 h-5 ${sev.iconColor}`} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border ${sev.chip} text-[10px] uppercase tracking-[0.14em] font-bold`}>
                        <CatIcon className="w-2.5 h-2.5" /> {CATEGORY_LABEL[a.category] || a.category}
                      </span>
                      {isUnread && (
                        <span
                          data-testid={`alert-unread-dot-${a.id}`}
                          className="w-2 h-2 rounded-full bg-yellow-400 shadow-[0_0_0_3px_rgba(250,204,21,0.18)]"
                          aria-label="Unread"
                        />
                      )}
                      <span className="text-[10.5px] text-white/35 ml-auto">
                        {new Date(a.created_at).toLocaleString()}
                      </span>
                    </div>
                    <h3 className="text-[15px] sm:text-[15.5px] font-bold text-white leading-snug tracking-tight">
                      {a.title}
                    </h3>
                    <p className="mt-1.5 text-[13px] text-white/65 leading-relaxed">
                      {a.body}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {a.action_url && (
                        <a
                          href={a.action_url}
                          onClick={() => { if (isUnread) markRead(a.id); }}
                          data-testid={`alert-action-${a.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black text-[12px] font-extrabold transition-all"
                        >
                          {a.action_label || 'Open'} <ChevronRight className="w-3 h-3" />
                        </a>
                      )}
                      {isUnread && (
                        <button
                          type="button"
                          onClick={() => markRead(a.id)}
                          disabled={busyId === a.id}
                          data-testid={`alert-read-${a.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 hover:border-yellow-500/40 text-white/70 hover:text-yellow-200 text-[12px] font-bold transition-all disabled:opacity-60"
                        >
                          Mark read
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => dismiss(a.id)}
                        disabled={busyId === a.id}
                        data-testid={`alert-dismiss-${a.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 hover:border-rose-500/40 text-white/55 hover:text-rose-300 text-[12px] font-bold transition-all disabled:opacity-60"
                      >
                        <X className="w-3 h-3" /> Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DashboardNotificationsTab;
